import React, { useEffect, useId, type Dispatch, type SetStateAction } from 'react';
import AssignAttribute from '~/components/AssignAttribute';
import InputError from '~/components/InputError';
import InputLabel from '~/components/InputLabel';
import Textarea from '~/components/Textarea';
import TextInput from '~/components/TextInput';
import { type TooltipProps } from '~/components/Tooltip';
import { Checkbox } from '~/components/ui/checkbox';
import { Field } from '~/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { AppListingType } from '~/config';
import { cn } from '~/lib/utils';
import { api, type RouterInputs } from '~/utils/api';
import enumToOptions from '~/utils/enumToOptions';
import { serverError } from '~/utils/serverError';

import { ArchiveMode, SocialMediaType } from '@prisma/client';

import { Frequency, type Errors } from './NewJobButton';

type UpdateScannerJobInput = RouterInputs['scanner']['updateScannerJob'];

type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type FormSelectFieldProps = {
  label: string;
  value?: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  tooltip?: React.ReactNode;
  tooltipProps?: Partial<TooltipProps>;
  error?: string;
  readOnly?: boolean;
  disabled?: boolean;
  className?: string;
};

function FormSelectField({
  label,
  value,
  onValueChange,
  options,
  placeholder = 'Select…',
  tooltip,
  tooltipProps,
  error,
  readOnly,
  disabled,
  className,
}: FormSelectFieldProps) {
  if (readOnly) {
    return (
      <TextInput
        label={label}
        value={options.find((o) => o.value === value)?.label || placeholder}
        tooltip={tooltip}
        tooltipProps={tooltipProps}
        readOnly
        disabled={disabled}
        className={className}
      />
    );
  }

  return (
    <Field className={cn('font-medium', className)}>
      <InputLabel label={label} tooltip={tooltip} tooltipProps={tooltipProps} />
      <Select value={value || undefined} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger
          aria-label={label}
          className="font-medium data-[placeholder]:font-normal"
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent position="popper">
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error ? <InputError error={error} /> : null}
    </Field>
  );
}

type PreferenceCheckboxProps = {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
};

function PreferenceCheckbox({
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}: PreferenceCheckboxProps) {
  const id = useId();

  return (
    <label
      htmlFor={id}
      className={cn(
        'flex min-h-14 items-start gap-3 px-4 py-3 transition-colors',
        disabled ? 'cursor-default' : 'hover:bg-muted/30 cursor-pointer',
      )}
    >
      <Checkbox
        id={id}
        className="mt-0.5 size-4 shrink-0"
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value) => onCheckedChange(value === true)}
      />
      <span className="min-w-0">
        <span className="text-foreground block text-sm font-medium">{label}</span>
        <span className="text-muted-foreground mt-0.5 block text-xs leading-5">{description}</span>
      </span>
    </label>
  );
}

type JobSettingsProps = {
  input: Partial<UpdateScannerJobInput>;
  setInput: React.Dispatch<React.SetStateAction<Partial<UpdateScannerJobInput>>>;
  frequency: Frequency;
  setFrequency: React.Dispatch<React.SetStateAction<Frequency>>;
  weekDay: number;
  setWeekDay: React.Dispatch<React.SetStateAction<number>>;
  monthDay: number;
  setMonthDay: React.Dispatch<React.SetStateAction<number>>;
  errors: Errors;
  setErrors: Dispatch<SetStateAction<Errors>>;
  isInfluencer: boolean;
  isReadOnly?: boolean;
};

const ARCHIVE_OFF = '__off__';

export default function JobSettings(props: JobSettingsProps) {
  const {
    input,
    setInput,
    frequency,
    setFrequency,
    weekDay,
    setWeekDay,
    monthDay,
    setMonthDay,
    errors,
    setErrors,
    isInfluencer,
  } = props;

  const { data: allDocumentEvaluationTypes } =
    api.policyEngine.getAllDocumentEvaluationTypes.useQuery();

  const isSocialMedia = !!input.socialMedia?.type;

  const { data: brands } = api.scanner.listBrands.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: authorized } = api.user.authorize.useQuery(undefined, {
    onError: serverError,
  });

  const { data: translationAvailable } = api.config.getTranslationAvailability.useQuery(
    undefined,
    { staleTime: Infinity, refetchOnWindowFocus: false },
  );

  const enabledFeatures = authorized?.user.tenant.enabledFeatures;

  const onChangeHandler = <T extends keyof UpdateScannerJobInput>(
    key: T,
    value: UpdateScannerJobInput[T],
  ) => {
    if (key === 'name' || key === 'documentEvaluationTypeId' || key === 'urls') {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
    setInput((prev) => ({
      ...(prev || {}),
      [key]: value,
    }));
  };

  const getTooltipProps = (
    tooltip: React.ReactNode,
  ): {
    tooltip: React.ReactNode;
    tooltipProps: Partial<TooltipProps>;
  } => ({
    tooltip,
    tooltipProps: { popoverProps: { disablePortal: true } },
  });

  let sourceTypeOptions: SelectOption[] = enumToOptions(SocialMediaType)
    .filter((option) => option.value !== SocialMediaType.LINKEDIN)
    .map((option) => ({ label: option.label, value: option.value }));

  if (!props.isInfluencer) {
    sourceTypeOptions = [{ label: 'Website', value: 'WEBSITE' }, ...sourceTypeOptions];
  }

  const selectDefaultAttributes = (brandId: string) => {
    const defaultAttributes = brands?.find((b) => b.id === brandId)?.defaultAttributes;
    if (defaultAttributes?.length) {
      setInput((prev) => {
        const attributeAssignments = [...(prev?.attributeAssignments || [])];
        defaultAttributes.forEach((defaultAttr) => {
          const existingAssignment = attributeAssignments.find(
            (a) => a.attributeId === defaultAttr.attributeId,
          );
          if (existingAssignment) {
            existingAssignment.value = Array.from(
              new Set([...existingAssignment.value, ...defaultAttr.value]),
            );
          } else {
            attributeAssignments.push({
              attributeId: defaultAttr.attributeId,
              value: defaultAttr.value,
              id: null,
            });
          }
        });
        return {
          ...(prev || {}),
          attributeAssignments,
        };
      });
    }
  };

  useEffect(() => {
    if (!input.attributeAssignments?.length && input.brandId && brands?.length) {
      selectDefaultAttributes(input.brandId);
    }
  }, [brands?.length]);

  const sourceTypeValue =
    (input?.socialMedia?.type as string | undefined) ||
    (input.appListing?.type as string | undefined) ||
    'WEBSITE';

  return (
    <div className="w-full">
      <div className="grid w-full grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
        <TextInput
          label="Name"
          value={input?.name || ''}
          onChange={(e) => onChangeHandler('name', e.target.value)}
          className="col-span-1"
          error={errors.name}
          readOnly={props.isReadOnly}
        />
        <FormSelectField
          label="Source Type"
          value={sourceTypeValue}
          options={sourceTypeOptions}
          readOnly={props.isReadOnly}
          className="col-span-1"
          onValueChange={(value) => {
            setErrors((prev) => ({ ...prev, username: undefined, urls: undefined }));
            if (value === 'WEBSITE') {
              setInput((prev) => ({
                ...prev,
                socialMedia: undefined,
                appListing: undefined,
              }));
            } else if (Object.values(AppListingType).includes(value as AppListingType)) {
              setInput((prev) => ({
                ...prev,
                socialMedia: undefined,
                appListing: {
                  type: value as AppListingType,
                  appId: '',
                },
              }));
            } else {
              onChangeHandler('socialMedia', {
                ...(input.socialMedia || {}),
                type: value as SocialMediaType,
              });
              onChangeHandler('appListing', undefined);
            }
            onChangeHandler('urls', undefined);
          }}
        />
        <FormSelectField
          className="col-span-1"
          label="Document Evaluation Type"
          {...getTooltipProps('Which review rules to run on each page.')}
          value={input?.documentEvaluationTypeId || undefined}
          placeholder="Select type"
          readOnly={props.isReadOnly}
          options={
            allDocumentEvaluationTypes?.documentEvaluationTypes.map((t) => ({
              label: t.name,
              value: t.id,
            })) || []
          }
          onValueChange={(value) => onChangeHandler('documentEvaluationTypeId', value)}
          error={errors.documentEvaluationTypeId}
        />
        <FormSelectField
          label="Brand"
          value={input.brandId || undefined}
          placeholder="Select brand"
          {...getTooltipProps('Which brand this scan belongs to.')}
          options={brands?.map((b) => ({ label: b.name, value: b.id })) || []}
          readOnly={props.isReadOnly}
          className="col-span-1"
          onValueChange={(value) => {
            onChangeHandler('brandId', value || null);
            if (value) {
              selectDefaultAttributes(value);
            }
          }}
        />
        <FormSelectField
          label="Frequency"
          value={frequency}
          {...getTooltipProps('How often this job should run.')}
          readOnly={props.isReadOnly}
          options={enumToOptions(Frequency).map((o) => ({ label: o.label, value: o.value }))}
          onValueChange={(value) => setFrequency(value as Frequency)}
          className="col-span-1"
        />
        {frequency === Frequency.WEEKLY && (
          <FormSelectField
            label="Day Of Week"
            value={String(weekDay)}
            options={Array.from({ length: 7 }, (_, i) => ({
              label: new Date(0, 0, i).toLocaleString('en-US', { weekday: 'long' }),
              value: String(i),
            }))}
            readOnly={props.isReadOnly}
            onValueChange={(value) => setWeekDay(parseInt(value, 10))}
            className="col-span-1"
          />
        )}
        {frequency === Frequency.MONTHLY && (
          <FormSelectField
            label="Day Of Month"
            value={String(monthDay)}
            readOnly={props.isReadOnly}
            options={Array.from({ length: 30 }, (_, i) => ({
              label: String(i + 1),
              value: String(i + 1),
            }))}
            onValueChange={(value) => setMonthDay(parseInt(value, 10))}
            className="col-span-1"
          />
        )}
        {((!isSocialMedia && !input.appListing) ||
          input?.socialMedia?.type === SocialMediaType.LINKEDIN ||
          input.socialMedia?.type === SocialMediaType.FACEBOOK) && (
          <Textarea
            label={`${input?.socialMedia?.type === SocialMediaType.LINKEDIN ? 'Company ' : ''} URLs`}
            className="sm:col-span-2"
            value={input.urls?.join('\n') || ''}
            readOnly={props.isReadOnly}
            onChange={(e) => onChangeHandler('urls', e.target.value.split('\n'))}
            {...getTooltipProps('One starting URL per line.')}
            error={errors.urls}
          />
        )}
        {input.appListing && (
          <TextInput
            className="sm:col-span-2"
            value={input.urls?.[0]}
            readOnly={props.isReadOnly}
            onChange={(e) => {
              const url = e.target.value;
              onChangeHandler('urls', [url]);
            }}
            {...getTooltipProps('Link to the app in the store.')}
            label="Store Link"
            error={errors.urls}
          />
        )}
        {!input.appListing && (
          <TextInput
            className="col-span-1"
            label="Min Tracked Pages"
            {...getTooltipProps('Fewest pages to check each run.')}
            inputProps={{ type: 'number' }}
            value={input.maxPages}
            readOnly={props.isReadOnly}
            onChange={(e) => onChangeHandler('maxPages', parseInt(e.target.value))}
          />
        )}
        {isSocialMedia &&
          input?.socialMedia?.type !== SocialMediaType.LINKEDIN &&
          input.socialMedia?.type !== SocialMediaType.FACEBOOK && (
            <TextInput
              label="Account Username"
              className="col-span-1"
              value={input.socialMedia?.username}
              readOnly={props.isReadOnly}
              onChange={(e) => {
                setErrors((prev) => ({ ...prev, username: undefined }));
                onChangeHandler('socialMedia', {
                  ...input.socialMedia!,
                  username: e.target.value,
                });
              }}
              error={errors.username}
            />
          )}
        {!isSocialMedia && !input.appListing && frequency !== Frequency.ONCE && (
          <>
            <TextInput
              className="col-span-1"
              label="Max Tracked Pages"
              {...getTooltipProps('Cap on pages kept across recurring scans.')}
              inputProps={{ type: 'number', min: 1 }}
              value={input.maxTrackedPages}
              readOnly={props.isReadOnly}
              onChange={(e) => onChangeHandler('maxTrackedPages', parseInt(e.target.value))}
            />
            <TextInput
              className="col-span-1"
              label="New Pages For Successive Scans"
              {...getTooltipProps('Extra new pages to find on later runs (recurring jobs only).')}
              inputProps={{ type: 'number', min: 0 }}
              value={input.newPagesForSuccessiveScans}
              readOnly={props.isReadOnly}
              onChange={(e) =>
                onChangeHandler('newPagesForSuccessiveScans', parseInt(e.target.value))
              }
            />
          </>
        )}
        {!isSocialMedia && !input.appListing && (
          <TextInput
            label="Max Depth"
            inputProps={{ type: 'number' }}
            {...getTooltipProps('How many link-clicks deep to crawl from each start URL.')}
            value={input.maxDepth}
            readOnly={props.isReadOnly}
            onChange={(e) => onChangeHandler('maxDepth', parseInt(e.target.value))}
          />
        )}

        <div className="sm:col-span-2 pt-2">
          <p className="text-muted-foreground mb-2.5 text-[11px] font-medium tracking-[0.08em] uppercase">
            Scan Options
          </p>
          <div className="border-border bg-card divide-border overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)] divide-y">
            {!isSocialMedia && !input.appListing ? (
              <PreferenceCheckbox
                label="Discover Sitemaps"
                description="Use sitemaps and robots.txt to improve crawl coverage."
                checked={!!input.useSitemaps}
                disabled={props.isReadOnly}
                onCheckedChange={(checked) => onChangeHandler('useSitemaps', checked)}
              />
            ) : null}
            <PreferenceCheckbox
              label="Save Findings To Decision Store"
              description="Keep scan findings available for later review."
              checked={!!input.showAllFeedbackInDecisionStore}
              disabled={props.isReadOnly}
              onCheckedChange={(checked) =>
                onChangeHandler('showAllFeedbackInDecisionStore', checked)
              }
            />
            {translationAvailable && (
              <PreferenceCheckbox
                label="Translate Content"
                description="Translate non-English content to English during the scan."
                checked={!!input.translationEnabled}
                disabled={props.isReadOnly}
                onCheckedChange={(checked) => onChangeHandler('translationEnabled', checked)}
              />
            )}
          </div>
        </div>
        {enabledFeatures?.includes('ARCHIVE_QUEUE') && (
          <FormSelectField
            label="Archive Mode"
            value={input.archiveMode || ARCHIVE_OFF}
            options={[
              { label: 'Off', value: ARCHIVE_OFF },
              ...enumToOptions(ArchiveMode)
                .filter((o) => {
                  if (isInfluencer) {
                    return o.value === ArchiveMode.ON_FOR_BRAND_MENTIONED;
                  }
                  return o.value === ArchiveMode.ON_FOR_ALL;
                })
                .map((o) => ({ label: o.label, value: o.value })),
            ]}
            readOnly={props.isReadOnly}
            onValueChange={(value) =>
              onChangeHandler('archiveMode', value === ARCHIVE_OFF ? null : (value as ArchiveMode))
            }
            className="col-span-1"
          />
        )}
      </div>
      <AssignAttribute
        id={input.id || null}
        type="scannerJob"
        disablePortal
        isReadOnly={props.isReadOnly}
        attributeAssignments={
          input.attributeAssignments?.map((a) => ({
            ...a,
            id: a.id || '',
          })) || []
        }
        onChange={(assignment) => {
          setInput((prev) => {
            let attributeAssignments = [...(prev?.attributeAssignments || [])];
            const attr = attributeAssignments?.find(
              (a) => a.attributeId === assignment.attributeId,
            );
            if (assignment._deleted) {
              attributeAssignments = attributeAssignments.filter((a) => a.id !== assignment.id);
            } else if (!attr) {
              attributeAssignments.push(assignment);
            } else {
              attr.value = assignment.value;
            }

            return {
              ...(prev || {}),
              attributeAssignments,
            };
          });
        }}
      />
    </div>
  );
}
